import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Smile, Frown, Printer } from 'lucide-react';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function FeelingsList() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const feelingsSatisfied = [
    {
      category: 'AFFECTIONATE',
      feelings: ['compassionate', 'friendly', 'loving', 'open hearted', 'sympathetic', 'tender', 'warm']
    },
    {
      category: 'CONFIDENT',
      feelings: ['empowered', 'open', 'proud', 'safe', 'secure']
    },
    {
      category: 'ENGAGED',
      feelings: ['absorbed', 'alert', 'curious', 'fascinated', 'interested', 'intrigued', 'stimulated']
    },
    {
      category: 'INSPIRED',
      feelings: ['amazed', 'awed', 'wonder']
    },
    {
      category: 'EXCITED',
      feelings: ['amazed', 'astonished', 'eager', 'energetic', 'enthusiastic', 'invigorated', 'lively', 'passionate', 'surprised']
    },
    {
      category: 'EXHILARATED',
      feelings: ['blissful', 'ecstatic', 'elated', 'exuberant', 'radiant', 'thrilled']
    },
    {
      category: 'GRATEFUL',
      feelings: ['appreciative', 'moved', 'thankful', 'touched']
    },
    {
      category: 'HOPEFUL',
      feelings: ['expectant', 'encouraged', 'optimistic']
    },
    {
      category: 'JOYFUL',
      feelings: ['amused', 'delighted', 'glad', 'happy', 'pleased', 'tickled']
    },
    {
      category: 'PEACEFUL',
      feelings: ['calm', 'clear headed', 'comfortable', 'centered', 'content', 'fulfilled', 'mellow', 'quiet', 'relaxed', 'relieved', 'satisfied', 'serene', 'still', 'tranquil', 'trusting']
    },
    {
      category: 'REFRESHED',
      feelings: ['enlivened', 'rejuvenated', 'renewed', 'rested', 'restored', 'revived']
    }
  ];

  const feelingsUnsatisfied = [
    {
      category: 'AFRAID',
      feelings: ['panicked', 'scared', 'suspicious', 'terrified', 'worried']
    },
    {
      category: 'ANNOYED',
      feelings: ['aggravated', 'frustrated', 'impatient', 'irritated']
    },
    {
      category: 'ANGRY',
      feelings: ['furious', 'outraged', 'resentful']
    },
    {
      category: 'AVERSION',
      feelings: ['disgusted', 'horrified', 'repulsed']
    },
    {
      category: 'CONFUSED',
      feelings: ['ambivalent', 'hesitant', 'lost', 'torn']
    },
    {
      category: 'DISCONNECTED',
      feelings: ['alienated', 'apathetic', 'bored', 'distant', 'distracted', 'numb', 'withdrawn']
    },
    {
      category: 'DISQUIET',
      feelings: ['agitated', 'alarmed', 'disturbed', 'restless', 'shocked', 'startled', 'surprised', 'uncomfortable', 'upset']
    },
    {
      category: 'EMBARRASSED',
      feelings: ['ashamed', 'guilty', 'self-conscious']
    },
    {
      category: 'FATIGUE',
      feelings: ['exhausted', 'tired', 'weary', 'worn out']
    },
    {
      category: 'PAIN',
      feelings: ['grief', 'heartbroken', 'hurt', 'lonely', 'miserable', 'regretful']
    },
    {
      category: 'SAD',
      feelings: ['depressed', 'despair', 'disappointed', 'discouraged', 'heavy hearted', 'hopeless', 'unhappy']
    },
    {
      category: 'TENSE',
      feelings: ['anxious', 'fidgety', 'irritable', 'nervous', 'overwhelmed', 'restless', 'stressed out']
    },
    {
      category: 'VULNERABLE',
      feelings: ['fragile', 'guarded', 'helpless', 'insecure', 'sensitive', 'shaky']
    },
    {
      category: 'YEARNING',
      feelings: ['envious', 'jealous', 'longing']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Feelings List</h1>
                <p className="text-slate-300 text-sm">Emotions when needs are satisfied or not</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 6</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          {/* Feelings When Needs ARE Satisfied */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Smile className="h-8 w-8 text-green-400" />
              <h2 className="text-2xl font-bold text-white">Feelings When Your Needs ARE Satisfied</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {feelingsSatisfied.map((group, idx) => (
                <div
                  key={idx}
                  className="bg-green-900/20 border border-green-700/30 rounded-lg overflow-hidden hover:border-green-600/50 transition-colors"
                  data-testid={`feelings-satisfied-${idx}`}
                >
                  <div className="bg-green-900/30 px-5 py-3 border-b border-green-700/30">
                    <h3 className="text-lg font-bold text-green-100">{group.category}</h3>
                  </div>
                  <div className="p-5">
                    <div className="flex flex-wrap gap-2">
                      {group.feelings.map((feeling, feelingIdx) => (
                        <span
                          key={feelingIdx}
                          className="bg-green-900/30 px-3 py-1.5 rounded-full text-green-100 text-sm border border-green-700/30"
                        >
                          {feeling}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feelings When Needs ARE NOT Satisfied */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Frown className="h-8 w-8 text-red-400" />
              <h2 className="text-2xl font-bold text-white">Feelings When Your Needs ARE NOT Satisfied</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {feelingsUnsatisfied.map((group, idx) => (
                <div
                  key={idx}
                  className="bg-red-900/20 border border-red-700/30 rounded-lg overflow-hidden hover:border-red-600/50 transition-colors"
                  data-testid={`feelings-unsatisfied-${idx}`}
                >
                  <div className="bg-red-900/30 px-5 py-3 border-b border-red-700/30">
                    <h3 className="text-lg font-bold text-red-100">{group.category}</h3>
                  </div>
                  <div className="p-5">
                    <div className="flex flex-wrap gap-2">
                      {group.feelings.map((feeling, feelingIdx) => (
                        <span
                          key={feelingIdx}
                          className="bg-red-900/30 px-3 py-1.5 rounded-full text-red-100 text-sm border border-red-700/30"
                        >
                          {feeling}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Practice Scenario */}
          <AIPracticeScenario pageTitle="Feelings List" pageNumber={6} />

          {/* Page Messaging */}
          <PageMessaging pageTitle="Feelings List" pageNumber={6} />
        </div>
      </div>
    </div>
  );
}
